import { useLayoutEffect, useRef, useState, type FC } from 'react';
import {
	StyleSheet,
	Text,
	View,
	type LayoutRectangle,
	type ScrollView,
	type StyleProp,
	type ViewStyle,
} from 'react-native';
import Animated, {
	Extrapolation,
	interpolate,
	useAnimatedScrollHandler,
	useAnimatedStyle,
	useSharedValue,
} from 'react-native-reanimated';
import { LinearGradient } from 'expo-linear-gradient';
import { SvgIconType } from '@thermenresorts/icons';
import type ApiLoyaltyResponse from 'src/core/models/api/responses/ApiLoyaltyResponse';
import Colors from 'src/core/utils/Colors';
import Constants from 'src/core/utils/Constants';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import Styles from 'src/core/utils/Styles';
import Translations from 'src/core/utils/Translations';
import { LoyaltyStatusVariant } from 'src/domain/models/LoyaltyStatusVariant';
import SkeletonBlock from 'src/presentation/components/atoms/SkeletonBlock';
import SvgIcon from 'src/presentation/components/atoms/SvgIcon';
import StarLvl0Icon from 'src/presentation/components/atoms/StarLvl0Icon';
import StarLvl1Icon from 'src/presentation/components/atoms/StarLvl1Icon';
import StarLvl2Icon from 'src/presentation/components/atoms/StarLvl2Icon';
import StarLvl3Icon from 'src/presentation/components/atoms/StarLvl3Icon';
import { PrimaryButton } from 'src/presentation/components/molecules/Button';

type StatusStep = {
	label: string;
	description: string;
	icon: string;
	isReached: boolean;
	isBathCard: boolean;
};

type StatusCircleProps = {
	icon: string;
	isBathCard: boolean;
	isReached: boolean;
};

interface Props {
	status: ApiLoyaltyResponse['status'] | undefined;
	variant: LoyaltyStatusVariant;
	style?: StyleProp<ViewStyle>;
	onPressBenefits?: () => void;
}

const FADE_WIDTH = Dimens.size31;
const SKELETON_STEP_COUNT = 3;

const fadeOpacity = (distanceToEdge: number) => {
	'worklet';

	return interpolate(distanceToEdge, [Dimens.size0, FADE_WIDTH], [Dimens.opacity0, Dimens.opacity100], {
		extrapolateLeft: Extrapolation.CLAMP,
		extrapolateRight: Extrapolation.CLAMP,
	});
};

const LoyaltyStatusCard: FC<Props> = ({ status, variant, style, onPressBenefits }) => {
	const statusProgressScrollView = useRef<ScrollView>(null);
	const [activeStatusRect, setActiveStatusRect] = useState<LayoutRectangle>();

	const scrollX = useSharedValue(0);
	const contentWidth = useSharedValue(0);
	const viewportWidth = useSharedValue(0);

	const onScroll = useAnimatedScrollHandler((event) => {
		scrollX.value = event.contentOffset.x;
		contentWidth.value = event.contentSize.width;
		viewportWidth.value = event.layoutMeasurement.width;
	});

	const fadeLeftStyle = useAnimatedStyle(() => ({ opacity: fadeOpacity(scrollX.value) }));
	const fadeRightStyle = useAnimatedStyle(() => ({
		opacity: fadeOpacity(contentWidth.value - viewportWidth.value - scrollX.value),
	}));

	useLayoutEffect(() => {
		if (statusProgressScrollView.current && activeStatusRect) {
			statusProgressScrollView.current.scrollTo({ x: activeStatusRect.x, animated: true });
		}
	}, [activeStatusRect]);

	const statusSteps: StatusStep[] =
		status?.progress.map((progress, index) => ({
			label: progress.text,
			description: progress.subtext,
			icon: progress.icon,
			isReached: index <= status.current_status,
			isBathCard: progress.icon === Constants.loyaltyStatusIconBathCard,
		})) ?? [];

	const lastReachedIndex = statusSteps.reduce((last, step, index) => (step.isReached ? index : last), 0);

	const connectorStyle = (index: number): ViewStyle | undefined => {
		const step = statusSteps[index];
		const nextStep = statusSteps[index + 1];

		if (!step || !nextStep) {
			return undefined;
		}

		if (nextStep.isReached) {
			return { backgroundColor: Colors.green700, height: Dimens.size5 };
		}

		if (nextStep.isBathCard && step.isReached) {
			return { backgroundColor: Colors.goldPale, height: Dimens.size3 };
		}

		return { backgroundColor: Colors.gray300, height: Dimens.size3 };
	};

	const isSummary = variant === LoyaltyStatusVariant.Summary;
	const statusMessage = status?.subtext ?? '';
	const summaryMessage = status?.subtext_short ?? statusMessage;

	if (status === undefined) {
		return <LoyaltyStatusSkeleton isSummary={isSummary} showsButton={onPressBenefits !== undefined} style={style} />;
	}

	return (
		<View style={[styles.card, Styles.card.shadowSoft, style]}>
			{isSummary ? (
				<View style={styles.summaryHeader}>
					<View style={styles.summaryHeadingRow}>
						<SvgIcon name={SvgIconType.starOutline} size={Dimens.iconSize18} tintColor={Colors.gray900} />
						<Text style={styles.summaryHeading}>{Translations.tabsThermenfans}</Text>
					</View>
					<Text style={styles.summaryMessage}>{summaryMessage}</Text>
				</View>
			) : (
				<View style={styles.pageHeader}>
					<Text style={styles.pageHeading}>{status ? `${status.name},\n${status.title_suffix}` : ''}</Text>
					<Text style={styles.pageMessage}>{statusMessage}</Text>
				</View>
			)}

			<View style={styles.statusContainer}>
				<Animated.ScrollView
					horizontal={true}
					ref={statusProgressScrollView}
					scrollEventThrottle={16}
					showsHorizontalScrollIndicator={false}
					style={styles.statusScrollView}
					onContentSizeChange={(width) => {
						contentWidth.value = width;
					}}
					onLayout={(event) => {
						viewportWidth.value = event.nativeEvent.layout.width;
					}}
					onScroll={onScroll}
				>
					<View style={styles.statusRow}>
						{statusSteps.map((step, index) => (
							<View
								key={step.label}
								style={styles.statusStep}
								onLayout={(event) => {
									if (index > 0 && index === lastReachedIndex) {
										setActiveStatusRect(event.nativeEvent.layout);
									}
								}}
							>
								<View style={styles.statusStepCircleRow}>
									<View style={[styles.statusConnector, connectorStyle(index - 1)]} />
									<StatusCircle icon={step.icon} isBathCard={step.isBathCard} isReached={step.isReached} />
									<View style={[styles.statusConnector, connectorStyle(index)]} />
								</View>
								<Text style={styles.statusText}>{step.label}</Text>
								{step.description ? <Text style={styles.statusDescription}>{step.description}</Text> : null}
							</View>
						))}
					</View>
				</Animated.ScrollView>
				<Animated.View pointerEvents="none" style={[styles.fadeLeft, fadeLeftStyle]}>
					<LinearGradient
						colors={[Colors.whiteCC, Colors.white00]}
						end={{ x: 1, y: 0 }}
						start={{ x: 0, y: 0 }}
						style={styles.fade}
					/>
				</Animated.View>
				<Animated.View pointerEvents="none" style={[styles.fadeRight, fadeRightStyle]}>
					<LinearGradient
						colors={[Colors.whiteCC, Colors.white00]}
						end={{ x: 0, y: 0 }}
						start={{ x: 1, y: 0 }}
						style={styles.fade}
					/>
				</Animated.View>
			</View>

			{isSummary && onPressBenefits ? (
				<View style={styles.buttonContainer}>
					<PrimaryButton label={Translations.loyaltyDiscoverMyBenefits} onPress={onPressBenefits} />
				</View>
			) : null}
		</View>
	);
};

const LoyaltyStatusSkeleton: FC<{ isSummary: boolean; showsButton: boolean; style?: StyleProp<ViewStyle> }> = ({
	isSummary,
	showsButton,
	style,
}) => (
	<View
		accessibilityRole="progressbar"
		accessibilityState={{ busy: true }}
		accessible={true}
		style={[styles.card, Styles.card.shadowSoft, style]}
	>
		{isSummary ? (
			<View style={styles.summaryHeader}>
				<View style={styles.summaryHeadingRow}>
					<SvgIcon name={SvgIconType.starOutline} size={Dimens.iconSize18} tintColor={Colors.gray900} />
					<Text style={styles.summaryHeading}>{Translations.tabsThermenfans}</Text>
				</View>
				<SkeletonBlock height={Dimens.size14} width={Dimens.size120} />
			</View>
		) : (
			<View style={[styles.pageHeader, styles.skeletonPageHeader]}>
				<SkeletonBlock height={Dimens.size24} width={Dimens.size200} />
				<SkeletonBlock height={Dimens.size14} width={Dimens.size140} />
			</View>
		)}

		<View style={[styles.statusContainer, styles.statusRow]}>
			{Array.from({ length: SKELETON_STEP_COUNT }, (_, index) => (
				<View key={index} style={styles.statusStep}>
					<View style={styles.statusStepCircleRow}>
						<View style={[styles.statusConnector, styles.skeletonConnector]} />
						<SkeletonBlock height={Dimens.size60} round={true} width={Dimens.size60} />
						<View style={[styles.statusConnector, styles.skeletonConnector]} />
					</View>
					<SkeletonBlock height={Dimens.size12} style={styles.skeletonStatusText} width={Dimens.size64} />
					<SkeletonBlock height={Dimens.size10} style={styles.skeletonStatusDescription} width={Dimens.size80} />
				</View>
			))}
		</View>

		{isSummary && showsButton ? (
			<View style={styles.buttonContainer}>
				<SkeletonBlock height={Dimens.buttonHeight50} round={true} />
			</View>
		) : null}
	</View>
);

const StatusCircle: FC<StatusCircleProps> = ({ icon, isBathCard, isReached }) => {
	const backgroundColor = isReached ? Colors.green700 : isBathCard ? Colors.goldPale : Colors.gray300;
	const outlineColor = isReached ? Colors.green : isBathCard ? Colors.goldSoft : Colors.gray350;
	const sparkleColor = isReached ? Colors.goldLight : outlineColor;

	return (
		<View
			style={[
				styles.statusCircle,
				isBathCard ? styles.statusCircleBathCard : styles.statusCircleLevel,
				{ backgroundColor },
			]}
		>
			{isBathCard ? (
				<StarLvl3Icon
					height={Dimens.size36}
					outlineColor={outlineColor}
					sparkleColor={sparkleColor}
					starColor={isReached ? Colors.goldLight : Colors.white}
					width={Dimens.size64}
				/>
			) : icon === Constants.loyaltyStatusIcon1 ? (
				<StarLvl1Icon
					height={Dimens.iconSize30}
					outlineColor={outlineColor}
					sparkleColor={sparkleColor}
					width={Dimens.size26}
				/>
			) : icon === Constants.loyaltyStatusIcon2 ? (
				<StarLvl2Icon
					height={Dimens.iconSize30}
					outlineColor={outlineColor}
					sparkleColor={sparkleColor}
					width={Dimens.size40}
				/>
			) : icon === Constants.loyaltyStatusIcon3 ? (
				<StarLvl3Icon
					height={Dimens.iconSize30}
					outlineColor={outlineColor}
					sparkleColor={sparkleColor}
					width={Dimens.size53}
				/>
			) : (
				<StarLvl0Icon height={Dimens.iconSize30} width={Dimens.size31} />
			)}
		</View>
	);
};

const styles = StyleSheet.create({
	card: {
		backgroundColor: Colors.white,
		borderRadius: Dimens.borderRadius4,
		paddingTop: Dimens.padding24,
		paddingBottom: Dimens.padding16,
		gap: Dimens.gap16,
	},
	fade: {
		flex: Dimens.flex1,
	},
	fadeLeft: {
		position: 'absolute',
		left: Dimens.padding0,
		top: Dimens.padding0,
		bottom: Dimens.padding0,
		width: FADE_WIDTH,
	},
	fadeRight: {
		position: 'absolute',
		right: Dimens.padding0,
		top: Dimens.padding0,
		bottom: Dimens.padding0,
		width: FADE_WIDTH,
	},
	pageHeader: {
		paddingHorizontal: Dimens.padding24,
		gap: Dimens.gap16,
	},
	pageHeading: {
		fontSize: Dimens.fontSize20,
		color: Colors.gray900,
		textAlign: 'center',
		fontFamily: Fonts.ptSerif,
	},
	pageMessage: {
		fontSize: Dimens.fontSize14,
		fontFamily: Fonts.barlow,
		color: Colors.gray550,
		textAlign: 'center',
	},
	summaryHeader: {
		flexDirection: 'row',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		gap: Dimens.gap4,
		paddingHorizontal: Dimens.padding20,
	},
	summaryHeadingRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: Dimens.gap6,
	},
	summaryHeading: {
		fontSize: Dimens.fontSize18,
		lineHeight: Dimens.lineHeight24,
		color: Colors.gray900,
		fontFamily: Fonts.ptSerif,
	},
	summaryMessage: {
		flex: Dimens.flex1,
		fontSize: Dimens.fontSize13,
		lineHeight: Dimens.lineHeight17,
		fontFamily: Fonts.barlow,
		color: Colors.gray550,
		textAlign: 'right',
	},
	buttonContainer: {
		paddingHorizontal: Dimens.padding20,
	},
	statusText: {
		width: '100%',
		textAlign: 'center',
		paddingTop: Dimens.padding12,
		fontFamily: Fonts.barlowMedium,
		fontSize: Dimens.fontSize14,
		color: Colors.gray900,
	},
	statusDescription: {
		width: '100%',
		textAlign: 'center',
		paddingHorizontal: Dimens.padding10,
		paddingTop: Dimens.padding5,
		fontFamily: Fonts.barlow,
		fontSize: Dimens.fontSize11,
		color: Colors.gray550,
	},
	statusContainer: {
		position: 'relative',
		overflow: 'visible',
		marginBottom: Dimens.padding12,
	},
	statusStep: {
		width: Dimens.size96,
		alignItems: 'center',
	},
	statusStepCircleRow: {
		width: '100%',
		height: Dimens.size76,
		flexDirection: 'row',
		alignItems: 'center',
	},
	statusConnector: {
		flex: Dimens.flex1,
	},
	statusCircle: {
		borderRadius: Dimens.borderRadius99,
		justifyContent: 'center',
		alignItems: 'center',
	},
	statusCircleLevel: {
		width: Dimens.size60,
		height: Dimens.size60,
	},
	statusCircleBathCard: {
		width: Dimens.size76,
		height: Dimens.size76,
		shadowColor: Colors.black,
		shadowOffset: { width: Dimens.size0, height: Dimens.size4 },
		shadowOpacity: Dimens.opacity5,
		shadowRadius: Dimens.size4,
		elevation: Dimens.elevation2,
	},
	statusScrollView: {
		paddingRight: Dimens.padding1,
	},
	statusRow: {
		flexDirection: 'row',
	},
	skeletonPageHeader: {
		alignItems: 'center',
	},
	skeletonConnector: {
		height: Dimens.size3,
		backgroundColor: Colors.gray200,
	},
	skeletonStatusText: {
		marginTop: Dimens.padding12,
	},
	skeletonStatusDescription: {
		marginTop: Dimens.padding5,
	},
});

export default LoyaltyStatusCard;
